import React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * StatusIndicator - WCAG 2.1 AA Compliant Status Component
 * Colorblind-safe with patterns, icons, and text labels
 * 
 * Features:
 * - Color + Pattern + Icon triple encoding for accessibility
 * - ARIA live region announcements
 * - High contrast text (4.5:1 minimum)
 * - Keyboard navigable
 */

const statusConfig = {
  success: {
    bg: 'bg-semantic-successLight dark:bg-green-900/30',
    border: 'border-semantic-success',
    text: 'text-green-800 dark:text-green-400',
    icon: CheckCircle,
    pattern: 'rag-green-pattern',
    label: 'Good',
    ariaLabel: 'Status: Good'
  },
  warning: {
    bg: 'bg-semantic-warningLight dark:bg-amber-900/30',
    border: 'border-semantic-warning',
    text: 'text-amber-800 dark:text-amber-400',
    icon: AlertTriangle,
    pattern: 'rag-amber-pattern',
    label: 'Warning',
    ariaLabel: 'Status: Warning'
  },
  danger: {
    bg: 'bg-semantic-dangerLight dark:bg-red-900/30',
    border: 'border-semantic-danger',
    text: 'text-red-800 dark:text-red-400',
    icon: XCircle,
    pattern: 'rag-red-pattern',
    label: 'Critical',
    ariaLabel: 'Status: Critical'
  },
  info: {
    bg: 'bg-semantic-infoLight dark:bg-blue-900/30',
    border: 'border-semantic-info',
    text: 'text-blue-800 dark:text-blue-400',
    icon: Info,
    pattern: '',
    label: 'Info',
    ariaLabel: 'Status: Informational'
  },
  neutral: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    border: 'border-gray-400',
    text: 'text-gray-700 dark:text-gray-300',
    icon: Minus,
    pattern: '',
    label: 'Neutral',
    ariaLabel: 'Status: Neutral'
  },
  // Aliases for backward compatibility
  green: 'success',
  amber: 'warning',
  red: 'danger',
  error: 'danger',
  good: 'success',
  critical: 'danger'
};

const sizeConfig = {
  xs: {
    badge: 'px-1.5 py-0.5 text-tiny gap-1',
    icon: 12,
    dot: 'w-1.5 h-1.5'
  },
  sm: {
    badge: 'px-2 py-0.5 text-xs gap-1',
    icon: 14,
    dot: 'w-2 h-2'
  },
  md: {
    badge: 'px-2.5 py-1 text-sm gap-1.5',
    icon: 16,
    dot: 'w-2.5 h-2.5'
  },
  lg: {
    badge: 'px-3 py-1.5 text-base gap-2',
    icon: 18,
    dot: 'w-3 h-3'
  }
};

export const StatusBadge = ({
  status = 'neutral',
  size = 'sm',
  showIcon = true,
  showLabel = true,
  showPattern = false,
  label,
  className,
  animate = false,
  ...props
}) => {
  // Resolve aliases
  const resolvedStatus = typeof statusConfig[status] === 'string' 
    ? statusConfig[statusConfig[status]] 
    : statusConfig[status] || statusConfig.neutral;
  
  const sizeStyles = sizeConfig[size] || sizeConfig.sm;
  const Icon = resolvedStatus.icon;
  const displayLabel = label || resolvedStatus.label;
  
  return (
    <span
      role="status"
      aria-label={resolvedStatus.ariaLabel}
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        resolvedStatus.bg,
        resolvedStatus.border,
        resolvedStatus.text,
        showPattern && resolvedStatus.pattern,
        sizeStyles.badge,
        animate && 'animate-status-change',
        className
      )}
      {...props}
    >
      {showIcon && <Icon size={sizeStyles.icon} aria-hidden="true" />}
      {showLabel && <span>{displayLabel}</span>}
    </span>
  );
};

export const StatusDot = ({
  status = 'neutral',
  size = 'sm',
  pulse = false,
  showPattern = true,
  className,
  ...props
}) => {
  const resolvedStatus = typeof statusConfig[status] === 'string' 
    ? statusConfig[statusConfig[status]] 
    : statusConfig[status] || statusConfig.neutral;
  
  const sizeStyles = sizeConfig[size] || sizeConfig.sm;
  
  return (
    <span
      role="status"
      aria-label={resolvedStatus.ariaLabel}
      className={cn(
        'inline-block rounded-full border-2',
        resolvedStatus.bg,
        resolvedStatus.border,
        showPattern && resolvedStatus.pattern,
        sizeStyles.dot,
        pulse && 'animate-pulse',
        className
      )}
      {...props}
    />
  );
};

export const StatusCard = ({
  status = 'neutral',
  title,
  value,
  subtitle,
  showPattern = true,
  className,
  children,
  ...props
}) => {
  const resolvedStatus = typeof statusConfig[status] === 'string' 
    ? statusConfig[statusConfig[status]] 
    : statusConfig[status] || statusConfig.neutral;
  
  const Icon = resolvedStatus.icon;
  
  return (
    <div
      role="region"
      aria-label={`${title} - ${resolvedStatus.ariaLabel}`}
      className={cn(
        'relative p-4 rounded-2xl border-2 transition-all duration-200',
        resolvedStatus.bg,
        resolvedStatus.border,
        showPattern && resolvedStatus.pattern,
        'hover:shadow-md',
        className
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-medium', resolvedStatus.text)}>
            {title}
          </p>
          {value && (
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {value}
            </p>
          )}
          {subtitle && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {subtitle}
            </p>
          )}
        </div>
        <Icon 
          size={24} 
          className={cn(resolvedStatus.text, 'flex-shrink-0')} 
          aria-hidden="true" 
        />
      </div>
      {children}
    </div>
  );
};

// RAG Matrix Cell for tables
export const RAGCell = ({
  status = 'neutral',
  value,
  showPattern = true,
  className,
  ...props
}) => {
  const resolvedStatus = typeof statusConfig[status] === 'string' 
    ? statusConfig[statusConfig[status]] 
    : statusConfig[status] || statusConfig.neutral;
  
  return (
    <td
      role="cell"
      aria-label={`${value} - ${resolvedStatus.ariaLabel}`}
      className={cn(
        'px-3 py-2 text-center font-medium border border-gray-200 dark:border-gray-700',
        resolvedStatus.bg,
        resolvedStatus.text,
        showPattern && resolvedStatus.pattern,
        className
      )}
      {...props}
    >
      {value}
    </td>
  );
};

// Real-time status indicator with live announcements
export const LiveStatusIndicator = ({
  status = 'success',
  label = 'Live',
  className
}) => {
  const resolvedStatus = typeof statusConfig[status] === 'string' 
    ? statusConfig[statusConfig[status]] 
    : statusConfig[status] || statusConfig.success;
  
  return (
    <div 
      className={cn('flex items-center gap-2', className)}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className={cn(
          'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
          status === 'success' && 'bg-green-500',
          status === 'warning' && 'bg-amber-500',
          status === 'danger' && 'bg-red-500'
        )} />
        <span className={cn(
          'relative inline-flex rounded-full h-2.5 w-2.5',
          status === 'success' && 'bg-green-500',
          status === 'warning' && 'bg-amber-500',
          status === 'danger' && 'bg-red-500'
        )} />
      </span>
      <span className={cn('text-sm font-medium', resolvedStatus.text)}>
        {label}
      </span>
    </div>
  );
};

export default StatusBadge;
